import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ProjectPageRedesignProvider } from 'components/ProjectPageRedesign/ProjectPageRedesignContext';
import ImagePreviewDialog from 'scenes/ProjectGallery/components/imagePreviewDialog/ImagePreviewDialog';
import { IMAGE_PREVIEW_CAPABILITIES_VIEW_ONLY } from 'scenes/ProjectGallery/components/imagePreviewDialog/imagePreviewCapabilities';
import { IMAGE_TARGET_TYPE } from './ActivityLogImageThumb';

const NO_EDIT_RIGHTS = [];

/**
 * The file permission the image preview reads, shaped from an image the API
 * says the viewer may see (as the chat shapes its attachments). No project:
 * the preview opens view-only, so nothing reads or writes the gallery's
 * data, and opening it here logs no view of the image. `known` (id -> file
 * permission) keeps each one's identity, since the preview reloads the image
 * whenever the object it is given changes.
 */
export const toPreviewFilePermission = (item, image, known) => {
  const id = item.targetId;
  const name = image.name || item.targetName || '';
  const cached = known && known.get(id);
  if (
    cached &&
    cached.name === name &&
    cached.file.mimeType === image.mimeType &&
    cached.rotation === (image.rotation || 0)
  ) {
    return cached;
  }
  const filePermission = {
    id,
    name,
    file: { mimeType: image.mimeType || '' },
    rotation: image.rotation || 0,
    createdAt: item.createdAt,
  };
  if (known) known.set(id, filePermission);
  return filePermission;
};

/**
 * The image preview of an activity-log list (the day dialog's, the project
 * dialog's): `items` in the order the list shows them, `images` the API's
 * verdicts (file permission id -> { status, name, mimeType, rotation }).
 * The preview pages through the images the viewer may see, once each, in
 * that order. Returns `{ previewImages, previewed, openImage(item),
 * setPreviewId(id), closePreview() }`; render it with ActivityImagePreview.
 */
export const useActivityImagePreview = (items, images) => {
  // The image open in the preview (its file permission id), or null.
  const [previewId, setPreviewId] = useState(null);
  const knownPreviewsRef = useRef(new Map());

  const previewImages = useMemo(() => {
    const seen = new Set();
    const list = [];
    (items || []).forEach((item) => {
      const image = item.targetId && images?.[item.targetId];
      if (
        item.targetType !== IMAGE_TARGET_TYPE ||
        image?.status !== 'AVAILABLE' ||
        seen.has(item.targetId)
      ) {
        return;
      }
      seen.add(item.targetId);
      list.push(toPreviewFilePermission(item, image, knownPreviewsRef.current));
    });
    return list;
  }, [items, images]);

  const previewed = previewId
    ? previewImages.find((image) => image.id === previewId) || null
    : null;

  const openImage = useCallback((item) => setPreviewId(item.targetId), []);
  const closePreview = useCallback(() => setPreviewId(null), []);

  return { previewImages, previewed, openImage, setPreviewId, closePreview };
};

/**
 * The gallery's image preview, view-only, over an activity-log dialog (see
 * useActivityImagePreview), while `open` and an image is chosen. Always in
 * its classic look: it is no redesigned dialog, wherever it opens from.
 */
export function ActivityImagePreview({ open, preview }) {
  if (!open || !preview.previewed) return null;
  return (
    <ProjectPageRedesignProvider value={false}>
      <ImagePreviewDialog
        open
        filePermission={preview.previewed}
        mediaFilePermissions={preview.previewImages}
        filePermissionIdsUserHasPermissionToEdit={NO_EDIT_RIGHTS}
        capabilities={IMAGE_PREVIEW_CAPABILITIES_VIEW_ONLY}
        onNavigate={preview.setPreviewId}
        onClose={preview.closePreview}
      />
    </ProjectPageRedesignProvider>
  );
}

ActivityImagePreview.propTypes = {
  open: PropTypes.bool,
  preview: PropTypes.shape({
    previewImages: PropTypes.array.isRequired,
    previewed: PropTypes.object,
    setPreviewId: PropTypes.func.isRequired,
    closePreview: PropTypes.func.isRequired,
  }).isRequired,
};
